import React from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { AsyncSelect } from '../ReactSelect';
import { getNameLikeCondition } from '../../helpers/search';
import { gql } from '@/modes/company/gql';
import { ResultOf } from 'gql.tada';

export const FETCH_WATER_SUPPLIERS = gql(`
  query WaterSuppliers($where: JSON, $orderBy: [ColumnOrder]) {
    waterSuppliers(where: $where, limit: null, orderBy: $orderBy) {
      profileId
      hasUser
      acceptEmail
      profile {
        id
        name
        contactAddress {
          state
        }
      }
    }
  }
`);

export type WaterSupplier = ResultOf<
  typeof FETCH_WATER_SUPPLIERS
>['waterSuppliers'][number];

type WaterSupplierPickerProps = {
  waterSupplierId?: string | null;
  onChange: (waterSupplier: WaterSupplier | null) => void;
  placeholder?: string;
  isClearable?: boolean;
};

const WaterSupplierPicker = ({
  waterSupplierId,
  onChange,
  placeholder,
  isClearable,
}: WaterSupplierPickerProps): JSX.Element => {
  const client = useApolloClient();

  const { error: itemError, data: itemData } = useQuery<{
    waterSuppliers: WaterSupplier[];
  }>(FETCH_WATER_SUPPLIERS, {
    variables: {
      where: {
        profileId: waterSupplierId,
      },
    },
    skip: !waterSupplierId,
  });

  const selected: WaterSupplier | undefined = itemData?.waterSuppliers[0];

  if (itemError) {
    throw itemError;
  }

  const loadOptionsFunc = async (inputValue: string) => {
    const res = await client.query({
      query: FETCH_WATER_SUPPLIERS,
      variables: {
        where: inputValue
          ? {
              'profile.name': getNameLikeCondition(inputValue),
            }
          : undefined,
        orderBy: [{ col: 'profile.name', dir: 'ASC' }],
      },
    });

    if (res.data?.waterSuppliers) {
      if (!selected && res.data.waterSuppliers.length === 1) {
        onChange(res.data.waterSuppliers[0]);
      }
      const groups = res.data.waterSuppliers
        .reduce(
          (groups, waterSupplier) => {
            const groupLabel =
              waterSupplier.profile.contactAddress?.state ?? 'Unknown';
            let group = groups.find((g) => g.label === groupLabel);
            if (!group) {
              group = {
                label: groupLabel,
                options: [],
              };
              groups.push(group);
            }
            group.options.push(waterSupplier);
            return groups;
          },
          [] as { label: string; options: WaterSupplier[] }[]
        )
        .sort((a, b) => a.label.localeCompare(b.label));
      return groups.length === 1 ? groups[0].options : groups;
    }

    return [];
  };

  return (
    <AsyncSelect
      cacheOptions
      value={selected}
      loadOptions={loadOptionsFunc}
      defaultOptions
      isClearable={isClearable}
      noOptionsMessage={() => null}
      menuPlacement="auto"
      onChange={(selected: WaterSupplier | null) => {
        if (!selected) {
          onChange(null);
        } else {
          onChange(selected);
        }
      }}
      placeholder={placeholder ?? 'Select water supplier...'}
      getOptionLabel={(waterSupplier) => waterSupplier.profile.name!}
      getOptionValue={(waterSupplier) => waterSupplier.profileId}
    />
  );
};

export default WaterSupplierPicker;
